import { BLANK_VALUE } from '../common/ColumnFilter';

/**
 * Column definitions for the daily and monthly reports.
 *
 * `text` is what the cell and the filter list show. `sortValue` is what a header
 * click orders by — for an hours column that is the raw seconds already on the
 * row, never the '9h 00m' string, because a string sort files 10h under 1.
 */
const employee = {
    key: 'name', label: 'Employee', filterable: true,
    text: (r) => r.name,
    sortValue: (r) => (r.name || '').toLowerCase(),
};

const department = {
    key: 'department', label: 'Department', filterable: true,
    text: (r) => r.department || '',
    sortValue: (r) => (r.department || '').toLowerCase(),
};

const daysTracked = {
    key: 'daysTracked', label: 'Days Tracked', filterable: true,
    text: (r) => String(r.daysTracked || 0),
    sortValue: (r) => r.daysTracked || 0,
};

const firstActive = {
    key: 'firstActiveTime', label: 'First Active', filterable: true,
    text: (r) => r.firstActiveTime || '',
    sortValue: (r) => r.firstActiveTime || '',
};

const lastActive = {
    key: 'lastActiveTime', label: 'Last Active', filterable: true,
    text: (r) => r.lastActiveTime || '',
    sortValue: (r) => r.lastActiveTime || '',
};

const activeHours = {
    key: 'activeHours', label: 'Active Hours', filterable: true,
    text: (r) => r.activeHours,
    sortValue: (r) => r.activeSeconds || 0,
};

const idleHours = {
    key: 'idleHours', label: 'Idle Hours', filterable: true,
    text: (r) => r.idleHours,
    sortValue: (r) => r.idleSeconds || 0,
};

const awayHours = {
    key: 'awayHours', label: 'Away', filterable: true,
    title: 'Away: time the agent was locked/off or otherwise not actively working.',
    text: (r) => r.awayHours,
    sortValue: (r) => r.awaySeconds || 0,
};

const totalHours = {
    key: 'totalHours', label: 'Total Working Hours', filterable: true,
    text: (r) => r.totalHours,
    sortValue: (r) => r.totalSeconds || 0,
};

const averageActive = {
    key: 'averageActive', label: 'Avg Active/Day', filterable: true,
    text: (r) => r.averageActive,
    // Average is active seconds over days tracked; sort by that, not the string.
    sortValue: (r) => (r.daysTracked ? (r.activeSeconds || 0) / r.daysTracked : 0),
};

const utilisation = {
    key: 'utilisation', label: 'Active %', filterable: true,
    text: (r) => (r.tracked ? `${r.utilisation}%` : 'No data'),
    sortValue: (r) => (r.tracked ? r.utilisation : -1),
};

const devices = {
    key: 'deviceCount', label: 'Devices', filterable: true,
    text: (r) => String(r.deviceCount || 0),
    sortValue: (r) => r.deviceCount || 0,
};

export const COLUMN_DEFS = {
    daily: [
        employee, department, firstActive, lastActive,
        activeHours, idleHours, awayHours, totalHours, utilisation, devices,
    ],
    monthly: [
        employee, department, daysTracked, firstActive, lastActive,
        activeHours, idleHours, awayHours, totalHours, averageActive, utilisation,
    ],
};

const defByKey = (key) => [...COLUMN_DEFS.daily, ...COLUMN_DEFS.monthly].find((c) => c.key === key);

/** The value a row contributes to a column's filter list. */
function filterValue(row, key) {
    const def = defByKey(key);
    const text = def ? def.text(row) : '';
    return text === '' || text === null || text === undefined ? BLANK_VALUE : text;
}

/**
 * Distinct displayed values for one column, with counts — the list the Excel
 * filter panel renders. Computed from the loaded rows, because the report
 * already returns every row in one response and a second source of truth for
 * "which values exist" would only be a way for the two to disagree.
 */
export function optionsFor(rows, key) {
    const counts = new Map();
    rows.forEach((row) => {
        const value = filterValue(row, key);
        counts.set(value, (counts.get(value) || 0) + 1);
    });

    return [...counts.entries()]
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => {
            if (a.value === BLANK_VALUE) return 1;
            if (b.value === BLANK_VALUE) return -1;
            return String(a.value).localeCompare(String(b.value), undefined, { numeric: true });
        });
}

/** Every applied column filter, ANDed. An empty selection means "no filter". */
export function applyFilters(rows, filters = {}) {
    const active = Object.entries(filters).filter(([, values]) => values && values.length);
    if (!active.length) return rows;

    return rows.filter((row) => active.every(([key, values]) => values.includes(filterValue(row, key))));
}

/** Sort a copy of the rows. `sort` is { key, direction } or null. */
export function applySort(rows, sort) {
    if (!sort || !sort.key) return rows;
    const def = defByKey(sort.key);
    if (!def) return rows;

    const factor = sort.direction === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) => {
        const av = def.sortValue(a);
        const bv = def.sortValue(b);
        if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * factor;
        return String(av).localeCompare(String(bv), undefined, { numeric: true }) * factor;
    });
}
